import React, { useEffect, useState, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import exportingModule from "highcharts/modules/exporting";
import accessibilityModule from "highcharts/modules/accessibility";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

// Initialize modules
HighchartsMore(Highcharts);
exportingModule(Highcharts);
accessibilityModule(Highcharts);

const AdvancedPolarChart = ({ selectedGlobalYear, selectedGlobalMonth, currency = "₹" }) => {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [monthlyData, setMonthlyData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(selectedGlobalMonth || new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
    const [loading, setLoading] = useState(true);

    const history = useHistory();

    const darkBg = "#1f1836";
    const lightBg = "#ffffff";
    const darkText = "#ffffff";
    const lightText = "#000000";
    const chartBg = isDarkMode ? darkBg : lightBg;
    const textColor = isDarkMode ? darkText : lightText;
    const borderColor = isDarkMode ? "#46465C" : "#e0e0e0";
    const secondaryNewBg = isDarkMode ? "rgba(59,130,246,0.1)" : "#eee";

    // Check login
    useEffect(() => {
        if (!localStorage.getItem("userData")) history.push("/login");
    }, [history]);

    const storedUserData = localStorage.getItem("userData");
    const groupId = storedUserData ? JSON.parse(storedUserData).groupId : "";

    // Detect dark mode
    useEffect(() => {
        const checkDark = () => document.documentElement.classList.contains("dark");
        setIsDarkMode(checkDark());
        const observer = new MutationObserver(() => setIsDarkMode(checkDark()));
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    // Sync with props
    useEffect(() => {
        if (selectedGlobalMonth) setSelectedMonth(selectedGlobalMonth);
    }, [selectedGlobalMonth]);

    useEffect(() => {
        if (selectedGlobalYear) setSelectedYear(selectedGlobalYear);
    }, [selectedGlobalYear]);

    // Fetch monthly data
    useEffect(() => {
        const fetchMonthlyData = async () => {
            if (!groupId) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const data = await apiService.getDailyExpenses(groupId, selectedYear, selectedMonth);
                setMonthlyData(data || []);
            } catch (err) {
                console.error(err);
                setMonthlyData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchMonthlyData();
    }, [groupId, selectedYear, selectedMonth]);

    // Prepare chart options with useMemo
    const chartOptions = useMemo(() => {
        if (monthlyData.length === 0) {
            return null;
        }

        const colors = Highcharts.getOptions().colors || [];
        const maxCredit = Math.max(...monthlyData.map((d) => d.credit || 0)) || 1;
        const maxDebit = Math.max(...monthlyData.map((d) => d.debit || 0)) || 1;

        const normalize = (value, maxValue) => Math.sqrt(value / maxValue) * 100;

        const creditSeries = monthlyData.map((entry, i) => [i + 1, 1, normalize(entry.credit || 0, maxCredit)]);
        const debitSeries = monthlyData.map((entry, i) => [i + 1, 2, normalize(entry.debit || 0, maxDebit)]);
        const totalDays = monthlyData.length;

        const scoreData = [
            { x: 1, low: 0, high: 5200, avg: 1733, highscore: 5000, topEarner: 'Credit', week: 1 },
            { x: 8, low: 0, high: 650, avg: 216, highscore: 500, topEarner: 'Credit', week: 2 },
            { x: 15, low: 0, high: 2200, avg: 733, highscore: 1200, topEarner: 'Credit', week: 3 },
            { x: 22, low: 0, high: 2330, avg: 777, highscore: 2000, topEarner: 'Credit', week: 4 }
        ];

        const weekLabelsData = Array(4).fill(0).map((_, index) => ({
            dataLabels: {
                format: 'Week {x}',
                enabled: true,
                inside: true,
                style: {
                    textOutline: 'none',
                    fontSize: '0.7em',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                },
                textPath: {
                    enabled: true,
                    attributes: {
                        startOffset: index % 3 ? '75%' : index % 2 ? '22%' : '28%',
                        dx: index % 2 ? '-2%' : '0%',
                        dy: index % 3 ? '2.8%' : '3.3%'
                    }
                }
            },
            x: index + 1,
            y: 1.5
        }));

        const creditColor = Highcharts.color(colors[9 % colors.length]).tweenTo(Highcharts.color(colors[0]), 0.25).toString();
        const debitColor = Highcharts.color(colors[9 % colors.length]).tweenTo(Highcharts.color(colors[8 % colors.length]), 0.65).toString();
        const gridLineColor = Highcharts.color(colors[1]).brighten(0.05).toString();

        return {
            accessibility: { enabled: false },
            chart: {
                polar: true,
                height: 400,
                backgroundColor: 'transparent',
                events: {
                    load: function () {
                        const midPane = this.pane[1];
                        (this).setMidPaneBg = function (background) {
                            midPane.update({ background });
                        };
                    }
                }
            },
            title: { text: undefined, style: { color: textColor, fontSize: '18px' } },
            subtitle: {
                text: undefined,
                useHTML: true,
                align: 'center',
                y: 35,
                verticalAlign: 'middle',
                style: { fontSize: '1.4em', color: textColor }
            },
            pane: [
                {
                    size: '80%',
                    innerSize: '75%',
                    startAngle: 0,
                    endAngle: 360,
                    background: [
                        {
                            borderColor: isDarkMode ? '#444' : '#ccc',
                            backgroundColor: {
                                radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
                                stops: [
                                    [0, isDarkMode ? '#1e1e1e' : '#fff'],
                                    [1, isDarkMode ? '#2a2a2a' : '#f4f4f4']
                                ]
                            },
                            innerRadius: '40%'
                        }
                    ]
                },
                {
                    size: '55%',
                    innerSize: '45%',
                    startAngle: 40.5,
                    endAngle: 319.5,
                    background: {
                        borderWidth: 0,
                        backgroundColor: {
                            radialGradient: [1, 0.25, 0.1],
                            stops: [[0, chartBg], [1, borderColor]]
                        },
                        outerRadius: '75%'
                    }
                },
                {
                    size: '100%',
                    innerSize: '88%',
                    startAngle: 0,
                    endAngle: 360,
                    background: {
                        borderWidth: 1,
                        borderColor: borderColor,
                        backgroundColor: secondaryNewBg,
                        innerRadius: '55%',
                        outerRadius: '100%'
                    }
                }
            ],
            xAxis: [
                {
                    pane: 0,
                    tickInterval: 1,
                    lineWidth: 0,
                    gridLineWidth: 0,
                    min: 1,
                    max: totalDays,
                    labels: { enabled: false }
                },
                {
                    pane: 1,
                    linkedTo: 0,
                    gridLineWidth: 0,
                    lineWidth: 0,
                    plotBands: Array(3).fill(7).map((weekendOffset, week) => ({
                        from: weekendOffset * (week + 1),
                        to: weekendOffset * (week + 1) - 1,
                        color: '#BBBAC5'
                    })),
                    min: 0,
                    max: totalDays,
                    labels: { enabled: false }
                },
                {
                    pane: 2,
                    tickAmount: 4,
                    tickInterval: 0.5,
                    gridLineWidth: 0,
                    lineWidth: 0,
                    min: 1,
                    max: 5,
                    labels: { enabled: false }
                }
            ],
            yAxis: [
                {
                    pane: 0,
                    gridLineWidth: 0.5,
                    gridLineDashStyle: 'LongDash',
                    gridLineColor: isDarkMode ? '#BBBAC5' : '#7a7a7a',
                    tickInterval: 1,
                    title: undefined,
                    labels: { enabled: false },
                    min: 1,
                    max: 3
                },
                {
                    pane: 1,
                    reversed: true,
                    gridLineWidth: 0,
                    tickInterval: 100,
                    min: 0,
                    max: 400,
                    title: undefined,
                    labels: { enabled: false }
                },
                {
                    pane: 2,
                    tickInterval: 0.25,
                    gridLineWidth: 0,
                    gridLineColor: gridLineColor,
                    min: -3,
                    max: 1,
                    title: undefined,
                    labels: { enabled: false }
                }
            ],
            legend: {
                enabled: true,
                floating: true,
                layout: 'vertical',
                verticalAlign: 'middle',
                align: 'center',
                backgroundColor: 'transparent',
                borderRadius: 14,
                borderColor: 'transparent',
                borderWidth: 0,
                itemStyle: { color: textColor, fontSize: '0.8em' },
                itemHoverStyle: { color: isDarkMode ? '#BBBAC5' : '#333', fontSize: '0.9em' }
            },
            tooltip: {
                useHTML: true,
                formatter: function () {
                    const record = monthlyData[this.point.x - 1];
                    const formattedAmount = this.point.z?.toLocaleString("en-IN") ?? 0;

                    if (this.series.type === 'bubble') {
                        return `
                            <div style="padding:6px 10px;">
                              <div style="font-weight:600; font-size:14px; margin-bottom:4px;">${this.series.name}</div>
                              <div><b>Date:</b> ${record?.date || '-'}</div>
                              <div><b>Day:</b> ${this.point.x}</div>
                              <div><b>Amount:</b> ${currency} ${formattedAmount}</div>
                            </div>
                          `;
                    }

                    if (this.series.type === 'columnrange') {
                        const week = this.point.week || 1;
                        const startIndex = (week - 1) * 7;
                        const endIndex = Math.min(startIndex + 6, monthlyData.length - 1);
                        let totalCredit = 0;
                        let totalDebit = 0;
                        for (let i = startIndex; i <= endIndex; i++) {
                            totalCredit += monthlyData[i]?.credit || 0;
                            totalDebit += monthlyData[i]?.debit || 0;
                        }
                        const startDate = monthlyData[startIndex]?.date || "-";
                        const endDate = monthlyData[endIndex]?.date || "-";
                        return `
                            <div style="padding:8px 10px; font-size:12px;">
                              <div style="font-weight:600; font-size:14px; margin-bottom:6px;">Week ${week}</div>
                              <div><b>Start Date:</b> ${startDate}</div>
                              <div><b>End Date:</b> ${endDate}</div>
                              <div><b>Total Credit:</b> ${currency} ${totalCredit.toLocaleString("en-IN")}</div>
                              <div><b>Total Debit:</b> ${currency} ${totalDebit.toLocaleString("en-IN")}</div>
                            </div>
                          `;
                    }
                }
            },
            series: [
                {
                    type: 'bubble',
                    name: 'Credit',
                    data: creditSeries,
                    color: creditColor,
                    marker: {
                        fillColor: creditColor,
                        lineColor: creditColor,
                        lineWidth: 2
                    },
                    zMin: 0,
                    zMax: 100,
                    minSize: "2px",
                    maxSize: "15px",
                    clip: false
                },
                {
                    type: 'bubble',
                    name: 'Debit',
                    data: debitSeries,
                    color: debitColor,
                    marker: {
                        fillColor: debitColor,
                        lineColor: debitColor,
                        lineWidth: 2
                    },
                    zMin: 0,
                    zMax: 100,
                    minSize: "2px",
                    maxSize: "15px",
                    clip: false
                },
                {
                    type: 'column',
                    name: 'Month',
                    data: weekLabelsData,
                    xAxis: 2,
                    yAxis: 2,
                    borderRadius: 50,
                    pointWidth: 1.2,
                    pointPlacement: 'between',
                    enableMouseTracking: false,
                    animation: false,
                    color: isDarkMode ? '#2CAFFE' : '#3A86FF'
                },
                {
                    type: 'columnrange',
                    name: 'Total',
                    data: scoreData,
                    xAxis: 1,
                    yAxis: 1,
                    shadow: false,
                    borderColor: 'transparent',
                    borderWidth: 2,
                    pointPlacement: 'on',
                    pointStart: 1,
                    animation: false,
                    color: isDarkMode ? '#2CAFFE' : '#3A86FF'
                }
            ]
        };
    }, [monthlyData, isDarkMode, textColor, chartBg, borderColor, secondaryNewBg, currency, selectedYear, selectedMonth]);

    return (
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 w-full mt-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-semibold text-gray-800 dark:text-white">Weekly Credit vs Debit</h2>
                <div className="flex gap-2">
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                        className="bg-gray-100 dark:bg-gray-700 dark:border-gray-600 text-xs rounded-md px-1 py-1"
                        disabled={loading}
                    >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>
                                {new Date(0, m - 1).toLocaleString("default", { month: "long" })}
                            </option>
                        ))}
                    </select>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        className="bg-gray-100 dark:bg-gray-700 dark:border-gray-600 text-xs rounded-md px-2 py-1"
                        disabled={loading}
                    >
                        {[2023, 2024, 2025].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>
            {loading ? (
                <div className="w-full h-64 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Loading chart data...</p>
                    </div>
                </div>
            ) : chartOptions ? (
                <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                    containerProps={{ style: { height: "400px" } }}
                />
            ) : (
                <div className="w-full h-64 flex items-center justify-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">No data available</p>
                </div>
            )}
        </div>
    );
};

export default AdvancedPolarChart;